import { useLayoutEffect, useRef, useState } from 'react';
import { Badge } from '@indipia/ui-core';
import { FieldReportCard } from './FieldReportCard';
import { WorkOrderCard } from './WorkOrderCard';
import type { Designation, PairRow } from './feedModel';

/**
 * 작업요청↔현장보고 페어 행(REQ-4-01) — 좌(작업요청) · 우(현장보고, 매칭된 건 전부 세로로 쌓음).
 * 매칭된 현장보고가 없으면 우측에 "작업요청 열람함/미열람 · 작업보고 미등록" 안내만 둔다(DEV_RULES).
 * 매칭 안 된 현장보고 단독(wo=null, 사장님 직접 작성 포함)은 좌측을 비워둔다 — 실측 목업·DEV_RULES 그대로
 * "사장님 직접 등록" 안내카드 문구는 만들지 않기로 함(사용자 확인 완료).
 * 연결선은 실측 목업(drawTreeConnectorsW)과 동일하게, WO카드 중심에서 각 FR슬롯 중심까지 개별
 * 꺾은선(⌐)을 실측 좌표로 그린다 — FR이 여러 건이면 선도 그만큼 갈라진다.
 */
export function PairRowView({
  row,
  readOnly,
  workTarget,
  savingFrId,
  getDesignation,
  onTogglePhoto,
  onEnterWork,
  onCancelWork,
  onSaveWork,
  onOpenPhoto,
}: {
  row: PairRow;
  /** 열람 전용(거래완료 현장) — 현장보고 카드의 사진 담기 숨김 */
  readOnly?: boolean;
  workTarget: { frId: number; mode: 'photo' } | null;
  savingFrId: number | null;
  getDesignation: (frId: number) => Designation;
  onTogglePhoto: (frId: number, idx: number) => void;
  onEnterWork: (frId: number) => void;
  onCancelWork: () => void;
  onSaveWork: () => void;
  onOpenPhoto: (frId: number, photoIdx: number) => void;
}) {
  const slotCount = row.frs.length || 1; // 미매칭이면 placeholder 1개가 슬롯

  const rowRef = useRef<HTMLDivElement | null>(null);
  const woRef = useRef<HTMLDivElement | null>(null);
  const frSlotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [branches, setBranches] = useState<{ woY: number; sY: number }[]>([]);
  const [svgH, setSvgH] = useState(0);
  // SVG 경로 계산용 숫자값 — 컨테이너 Tailwind 클래스 `w-14`(56px, D-07 §토큰 외 임의px 금지)와 반드시 일치시킨다.
  const CONN_W = 56;

  useLayoutEffect(() => {
    if (!row.wo) {
      setBranches([]);
      return;
    }
    const measure = () => {
      const rowEl = rowRef.current;
      const woEl = woRef.current;
      if (!rowEl || !woEl) return;
      const rowRect = rowEl.getBoundingClientRect();
      const woRect = woEl.getBoundingClientRect();
      const woY = woRect.top + woRect.height / 2 - rowRect.top;
      const next = frSlotRefs.current
        .filter((el): el is HTMLDivElement => el != null)
        .map((el) => {
          const r = el.getBoundingClientRect();
          return { woY, sY: r.top + r.height / 2 - rowRect.top };
        });
      setSvgH(rowRect.height);
      setBranches(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (rowRef.current) ro.observe(rowRef.current);
    if (woRef.current) ro.observe(woRef.current);
    frSlotRefs.current.forEach((el) => el && ro.observe(el));
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.wo, slotCount, row.frs]);

  return (
    <div ref={rowRef} className="flex items-start">
      <div className="min-w-0 flex-1" ref={row.wo ? woRef : undefined}>
        {row.wo ? <WorkOrderCard item={row.wo.wo} /> : <div className="h-full min-h-[1px]" />}
      </div>
      <div className="relative w-14 shrink-0 self-stretch" aria-hidden>
        <svg className="absolute left-0 top-0 overflow-visible" width={CONN_W} height={svgH}>
          {branches.map((b, i) => (
            <g key={i}>
              <path
                d={`M0,${b.woY} L${CONN_W / 2},${b.woY} L${CONN_W / 2},${b.sY} L${CONN_W},${b.sY}`}
                fill="none"
                stroke="var(--shell-hint)"
                strokeWidth={2}
              />
              <circle cx={0} cy={b.woY} r={3} fill="var(--shell-hint)" />
              <circle cx={CONN_W} cy={b.sY} r={3} fill="var(--shell-hint)" />
            </g>
          ))}
        </svg>
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        {row.frs.length === 0 ? (
            <div
              ref={(el) => (frSlotRefs.current[0] = el)}
              className="flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-border bg-surface px-4 py-3"
            >
              {(() => {
                const viewed = row.wo?.wo.recipients.find((r) => r.role === 'WORKER')?.viewed;
                return (
                  <>
                    <span className="shrink-0 text-body-md font-bold text-text-secondary">
                      {row.wo?.wo.recipients.find((r) => r.role === 'WORKER')?.partnerName ?? '-'}
                    </span>
                    <Badge status={viewed ? 'success' : 'danger'}>
                      작업요청 {viewed ? '열람함' : '미열람'}
                    </Badge>
                    <Badge status="warning">작업보고 미등록</Badge>
                  </>
                );
              })()}
            </div>
          ) : (
            row.frs.map((fr, idx) => (
              <div key={fr.fr.fieldReportId} ref={(el) => (frSlotRefs.current[idx] = el)}>
                <FieldReportCard
                  item={fr.fr}
                  readOnly={readOnly}
                  workMode={workTarget?.frId === fr.fr.fieldReportId ? workTarget.mode : null}
                  saving={savingFrId === fr.fr.fieldReportId}
                  designation={getDesignation(fr.fr.fieldReportId)}
                  onTogglePhoto={(idx2) => onTogglePhoto(fr.fr.fieldReportId, idx2)}
                  onEnterWork={() => onEnterWork(fr.fr.fieldReportId)}
                  onCancelWork={onCancelWork}
                  onSaveWork={onSaveWork}
                  onOpenPhoto={(photoIdx) => onOpenPhoto(fr.fr.fieldReportId, photoIdx)}
                />
              </div>
            ))
          )}
      </div>
    </div>
  );
}
